// src/components/vendor/VendorOverview.tsx
import React from 'react';
import { DollarSign, TrendingUp, Package, Ticket, Info } from 'lucide-react';
import './VendorTabs.css';

interface VendorStats {
    totalSales: number;
    totalCommission: number;
    availableBalance: number;
    activeCoupons: number;
    totalOrders: number;
}

interface Sale {
    _id: string;
    createdAt: string;
    vendorCommissionCents: number;
    paymentStatus: string;
}

interface VendorOverviewProps {
    stats: VendorStats | null;
    sales: Sale[];
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const VendorOverview: React.FC<VendorOverviewProps> = ({ stats, sales }) => {
    // Calcular vendas hoje
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const salesToday = sales.filter(sale => {
        const saleDate = new Date(sale.createdAt);
        return saleDate >= today && sale.paymentStatus === 'approved';
    }).length;

    const commissionToday = sales
        .filter(sale => {
            const saleDate = new Date(sale.createdAt);
            return saleDate >= today && sale.paymentStatus === 'approved';
        })
        .reduce((sum, sale) => sum + sale.vendorCommissionCents, 0);

    return (
        <div className="vendor-overview">
            <div className="vd-overview-header">
                <h2>Visão Geral</h2>
                <p className="vd-overview-subtitle">Acompanhe seu desempenho e comissões</p>
            </div>

            {/* Cards principais */}
            <div className="vd-stats-grid">
                <div className="vd-stat-card vd-highlight">
                    <div className="vd-stat-icon" style={{ background: '#10b981' }}>
                        <DollarSign size={28} />
                    </div>
                    <div className="vd-stat-info">
                        <span className="vd-stat-label">
                            Saldo Disponível
                            <span
                                className="vd-info-tooltip"
                                title="Comissões disponíveis após período de segurança de 7 dias"
                            >
                                <Info size={14} />
                            </span>
                        </span>
                        <span className="vd-stat-value">
                            R$ {((stats?.availableBalance || 0) / 100).toFixed(2)}
                        </span>
                        <span className="vd-stat-description">
                            Disponível para pagamento
                        </span>
                    </div>
                </div>

                <div className="vd-stat-card">
                    <div className="vd-stat-icon" style={{ background: '#3b82f6' }}>
                        <TrendingUp size={28} />
                    </div>
                    <div className="vd-stat-info">
                        <span className="vd-stat-label">Total em Comissões</span>
                        <span className="vd-stat-value">
                            R$ {((stats?.totalCommission || 0) / 100).toFixed(2)}
                        </span>
                        <span className="vd-stat-description">
                            Todas as comissões aprovadas
                        </span>
                    </div>
                </div>

                <div className="vd-stat-card">
                    <div className="vd-stat-icon" style={{ background: '#8b5cf6' }}>
                        <Package size={28} />
                    </div>
                    <div className="vd-stat-info">
                        <span className="vd-stat-label">Total de Vendas</span>
                        <span className="vd-stat-value">{stats?.totalOrders || 0}</span>
                        <span className="vd-stat-description">
                            Pedidos aprovados
                        </span>
                    </div>
                </div>

                <div className="vd-stat-card">
                    <div className="vd-stat-icon" style={{ background: '#f59e0b' }}>
                        <Ticket size={28} />
                    </div>
                    <div className="vd-stat-info">
                        <span className="vd-stat-label">Cupons Ativos</span>
                        <span className="vd-stat-value">{stats?.activeCoupons || 0}</span>
                        <span className="vd-stat-description">
                            Cupons e links criados
                        </span>
                    </div>
                </div>
            </div>

            {/* Vendas hoje */}
            <div className="vd-today-stats">
                <h3>Vendas de Hoje</h3>
                <div className="vd-today-grid">
                    <div className="vd-today-card">
                        <span className="vd-today-label">Pedidos</span>
                        <span className="vd-today-value">{salesToday}</span>
                    </div>
                    <div className="vd-today-card">
                        <span className="vd-today-label">Comissões</span>
                        <span className="vd-today-value">
                            R$ {(commissionToday / 100).toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorOverview;
